//
// PropertyTable
//
// A table of text-inputs, in minimal form. 3 columns: field label, field value, field
// unit.
// Adds drag-and-drop for rows.

import { observer } from 'mobx-react-lite'
import { useContext } from 'react'

import {
    AssetField,
    AssetListField,
    BarcodeField,
    BooleanField,
    ClassField,
    ClassListField,
    FieldpickerField,
    KeyValueField,
    KeyValueListField,
    RecordField,
    RecordListField,
    TextField,
    TextListField,
} from '.'

import { hasValuesOption } from '../../../../stores/data/utils'
import { hashData } from '../../../../utils/utils'
import { ItemTitle } from '../../../components'
import { LocalizedContext } from '../layoutbuilders/LocalizedContext'

const PropertyRow = observer(function PropertyRow({
    record,
    field,
    class_,
    worksheet,
    selectedfieldgid,
    onClickField,
    onContextMenuField,
    visited_gids,
}) {
    const context_is_localized = useContext(LocalizedContext)

    if (!field) {
        return null
    }

    let rowclasses = 'ws-propertytable-row'
    if (selectedfieldgid === field.gid) {
        rowclasses += ' cc-selected'
    }
    let renderkey = record.gid + '.' + field.gid + '.' + field.type
    if (field.options) {
        renderkey = renderkey + '.' + hashData(field.options)
    }

    const sharedFieldAttributes = {
        enabled: true,
        field,
        record,
        worksheet,
        renderkey,
        visited_gids,
    }

    let PropertyField
    switch (field.type) {
        case 'image':
        case 'file':
            PropertyField = (
                <AssetField
                    {...sharedFieldAttributes}
                    className="ws-tile"
                    component={{ style: 'item' }}
                />
            )
            break
        case 'imagelist':
        case 'filelist':
            PropertyField = (
                <AssetListField
                    {...sharedFieldAttributes}
                    component={{ style: 'item' }}
                />
            )
            break
        case 'record':
            PropertyField = (
                <RecordField
                    {...sharedFieldAttributes}
                    className="ws-tile"
                    component={{ style: 'item' }}
                />
            )
            break
        case 'recordlist':
            PropertyField = (
                <RecordListField
                    {...sharedFieldAttributes}
                    component={{ style: 'item' }}
                />
            )
            break
        case 'boolean':
            PropertyField = <BooleanField {...sharedFieldAttributes} />
            break
        case 'barcode':
            PropertyField = (
                <BarcodeField
                    {...sharedFieldAttributes}
                    component={{ style: 'plaintext' }}
                />
            )
            break
        case 'textlist':
            if (hasValuesOption(field)) {
                PropertyField = <KeyValueListField {...sharedFieldAttributes} />
            } else {
                PropertyField = <TextListField {...sharedFieldAttributes} />
            }
            break
        case 'fieldpicker':
            PropertyField = (
                <FieldpickerField
                    {...sharedFieldAttributes}
                    component={{ style: 'item' }}
                />
            )
            break
        case 'class':
            PropertyField = (
                <LocalizedContext.Provider
                    value={context_is_localized || field.is_localized}
                >
                    <ClassField
                        {...sharedFieldAttributes}
                        renderkey={renderkey + '.' + record.data_hash}
                        propertytable
                    />
                </LocalizedContext.Provider>
            )
            break
        case 'classlist':
            PropertyField = (
                <LocalizedContext.Provider
                    value={context_is_localized || field.is_localized}
                >
                    <ClassListField
                        {...sharedFieldAttributes}
                        renderkey={renderkey + '.' + record.data_hash}
                        propertytable
                    />
                </LocalizedContext.Provider>
            )
            break
        case 'textline':
            if (hasValuesOption(field)) {
                PropertyField = <KeyValueField {...sharedFieldAttributes} />
            } else {
                PropertyField = <TextField {...sharedFieldAttributes} />
            }
            break
        default:
            PropertyField = <TextField {...sharedFieldAttributes} />
    }

    const FieldRow = (
        <>
            <div className="ws-propertytable-cell ws-propertytable-cell-label">
                <ItemTitle item={field} ellipsisAfter={32} />

                {context_is_localized || field.is_localized ? (
                    <span className="language">{record.language}</span>
                ) : undefined}
            </div>
            <div
                className="ws-propertytable-cell ws-propertytable-cell-input"
                onContextMenu={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    e.nativeEvent.stopImmediatePropagation()
                }}
            >
                {PropertyField}
            </div>
        </>
    )

    return (
        <div
            className={rowclasses}
            key={field.gid}
            onClick={() => onClickField && onClickField(field.gid)}
            onContextMenu={e => onContextMenuField && onContextMenuField(e, field.gid)}
        >
            {FieldRow}
        </div>
    )
})

export const PropertyTable = observer(function PropertyTable(props) {
    const {
        className,
        record,
        fields,
        fromClass,
        selectedfieldgid,
        onClickField,
        onContextMenuField,
        worksheet,
        visited_gids,
    } = props

    const class_ = fromClass ? worksheet.classes.get(fromClass) : null

    const _onClickField = fieldgid => {
        onClickField && onClickField(fieldgid)
    }

    const _onContextMenuField = (e, fieldgid) => {
        onContextMenuField && onContextMenuField(e, fieldgid)
    }

    let Table
    if (fields && fields.length) {
        const Rows = fields.map(fieldgid => {
            const field = worksheet.fields.get(fieldgid)
            return (
                <PropertyRow
                    key={fieldgid}
                    record={record}
                    field={field}
                    class_={class_}
                    worksheet={worksheet}
                    selectedfieldgid={selectedfieldgid}
                    onClickField={() => _onClickField(fieldgid)}
                    onContextMenuField={e => _onContextMenuField(e, fieldgid)}
                    visited_gids={visited_gids}
                />
            )
        })
        let classes = 'ws-propertytable'
        if (className) classes += ' ' + className
        Table = <div className={classes}>{Rows}</div>
    }

    return Table
})
